import { AbstractHttp, ServiceMethod } from '@app/libs/services';
import { IService } from '@app/libs/services/BaseService';
import { apiConfig } from '@app/repositories/config';

export class TransactionService extends AbstractHttp {
  protected services: Record<string, IService> = {
    create: {
      uri: 'transactions',
      method: 'POST' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      contentType: 'application/json',
      params: {
        propertyValue: { required: true },
        depositAmount: { required: true },
        publishCertificateNumber: { required: true },
        newsId: { required: true },
        taxType: { required: true },
        depositDate: { required: true },
        transferPropertyDate: { required: true },
        transferPropertyDocDate: { required: true },
        notePayment: { required: false },
        note: { required: false },
      },
    },
    confirm: {
      uri: 'transactions/{transactionNo}/confirm',
      method: 'POST' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      contentType: 'application/json',
      params: {
        ownerCitizenNumber: { required: true },
        ownerMail: { required: true },
        ownerName: { required: true },
        transactionNo: { required: true },
        owners: { required: true },
      },
    },
    cancel: {
      uri: 'transactions/{transNo}',
      method: 'DELETE' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      contentType: 'application/json',
      params: {
        transNo: { required: true },
        cancelReason: { required: true },
      },
    },
    detail: {
      uri: 'transactions/{transNo}',
      method: 'GET' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      params: {
        transNo: { required: true },
      },
    },
    complete: {
      uri: 'transactions/{transNo}/complete',
      method: 'POST' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      contentType: 'application/json',
      params: {
        transNo: { required: true },
      },
    },
    search: {
      uri: 'transactions',
      method: 'GET' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      contentType: 'application/json',
      params: {
        transType: { required: false },
        keyword: { required: false },
        status: { required: false },
        fromDate: { required: false },
        toDate: { required: false },
        pageIndex: { required: false },
        pageSize: { required: false },
        propertiesOrder: { required: false },
      },
    },
    update: {
      uri: 'transactions/{transactionNo}',
      method: 'POST' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      contentType: 'application/json',
      params: {
        transactionNo: { required: true },
        depositAmount: { required: false },
        requestType: { required: true },
        notePayment: { required: false },
        note: { required: false },
        propertyValue: { required: false },
        depositDate: { required: false },
        depositTime: { required: false },
        taxType: { required: false },
        transferPropertyDate: { required: false },
        transferPropertyDocDate: { required: false },
        documents: { required: false },
      },
    },
    generateDocument: {
      uri: 'transactions/{transNo}/documents/{type}',
      method: 'GET' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      params: {
        transNo: { required: true },
        type: { required: true },
      },
      responseType: 'blob' as const,
    },

    requestSign: {
      uri: 'transactions/{transNo}/documents/{documentId}/requestSign',
      method: 'POST' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      params: {
        transNo: { required: true },
        documentId: { required: true },
      },
    },
    signDocument: {
      uri: 'transactions/{transNo}/documents/{documentId}/sign',
      method: 'POST' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      params: {
        transNo: { required: true },
        documentId: { required: true },
      },
    },
    rejectDocument: {
      uri: 'transactions/{transNo}/documents/{documentId}/reject',
      method: 'POST' as ServiceMethod,
      host: '/',
      prefix: 'api',
      version: 'v1',
      authRequire: true,
      params: {
        transNo: { required: true },
        documentId: { required: true },
      },
    },
  };

  create(data: any) {
    return this.send('create', { params: data });
  }

  confirm(data: any) {
    return this.send('confirm', {
      params: {
        ...data,
        ownerCitizenNumber: data.owners[0].citizenNumber,
        ownerMail: data.owners[0].email,
        ownerName: data.owners[0].name,
      },
    });
  }

  cancel(transNo: string, cancelReason: string) {
    return this.send('cancel', { params: { transNo, cancelReason } });
  }

  detail(transNo: string) {
    return this.send('detail', { params: { transNo } });
  }

  complete(transNo: string) {
    return this.send('complete', { params: { transNo } });
  }

  search(data: any) {
    return this.send('search', { params: data });
  }

  update(data: any) {
    return this.send('update', { params: { ...data, depositDate: data.depositTime } });
  }

  generateDocument(transNo: string, type: 'ASSIGNMENT_CONTRACT' | 'DEPOSIT_AGREEMENT') {
    return this.send('generateDocument', { params: { transNo, type } });
  }

  requestSign(transNo: string, documentId: string) {
    return this.send('requestSign', { params: { transNo, documentId } });
  }

  signDocument(transNo: string, documentId: string) {
    return this.send('signDocument', { params: { transNo, documentId } });
  }

  rejectDocument(transNo: string, documentId: string) {
    return this.send('rejectDocument', { params: { transNo, documentId } });
  }
}

export const transactionService = new TransactionService();
